import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { FaTrash } from 'react-icons/fa';

import config from '../../../config.js';
import { useFetchWrapper } from '../../../_helpers/fetchWrapper.js';
import { useT } from '../../../contexts/LocaleContext.jsx';
import { resolveMediaUrl } from '../../../_helpers/mediaUrl.js';
import { serverMessage } from '../../../_helpers/errorMessage.js';

import Modal from 'react-modal';
import Switch from '../../Switch.jsx';
import LoadingSpinner from '../../partial/LoadingSpinner.jsx';
import '../../../styles/partial/modal/CreateTagModal.css';

/** Icon types the picker and drag and drop both accept (ENG-509). */
const ICON_EXTENSIONS = ['.jpg', '.jpeg', '.png'];
const ICON_MIME_TYPES = ['image/jpeg', 'image/png'];
// The backend refuses a larger icon with 413 tag_icon_too_large (ENG-569);
// checking first means a big photo is refused before it is uploaded at all.
export const MAX_ICON_BYTES = 2 * 1024 * 1024;
export const isAcceptedIcon = (file) => {
  if (!file) return false;
  const name = String(file.name || '').toLowerCase();
  return ICON_MIME_TYPES.includes(file.type) || ICON_EXTENSIONS.some((ext) => name.endsWith(ext));
};

/** The tag list, whether the API answers `[...]` or `{ tags: [...], success }`. */
const tagList = (response) => (Array.isArray(response) ? response : Array.isArray(response?.tags) ? response.tags : []);

const CreateTagModal = ({ isOpen, onRequestClose, onSave, tag  }) => {
  const { bandid } = useParams();
  const t = useT();
  // The reset effect reads `t` through a ref so a locale switch while the
  // modal is open does not re-seed the form and discard in-progress edits.
  const tRef = useRef(t);
  tRef.current = t;
  const [tagId, setTagId] = useState(0);
  const [tagName, setTagName] = useState(t('editTags.modal.sampleName'));
  const [backgroundColor, setBackgroundColor] = useState('#ffffff');
  const [textColor, setTextColor] = useState('#000000');
  const [tagImage, setTagImage] = useState(null);
  const [tagImageUrl, setTagImageUrl] = useState(null);
  const [isPublic, setIsPublic] = useState(true);

  const [loading, setLoading] = useState(false);
  // Why the last save failed, shown in the still-open modal (ENG-509).
  const [error, setError] = useState(null);
  // Remove image on a saved tag is only marked here; the DELETE is sent on
  // Save, so Cancel leaves the icon in place (ENG-509).
  const [removeIconOnSave, setRemoveIconOnSave] = useState(false);

  const fetchWrapperRef = useRef(useFetchWrapper());
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      if (tag) {
        const tagId = tag.id;
        const tagWord = tag.word;
        const tagBgColor = tag.background_color;
        const tagTextColor = tag.text_color;
        const tagIsPublic = tag.is_public;
        const tagImageUrl = tag.image_url;
        
        setTagId(tagId);
        setTagName(tagWord);
        setBackgroundColor(tagBgColor);
        setTextColor(tagTextColor);
        setIsPublic(tagIsPublic);
        setTagImage(tag.TagImage || null);
        // The API stores the icon's full URL (ENG-509); resolveMediaUrl keeps
        // an absolute URL and prefixes a relative path.
        setTagImageUrl(tagImageUrl ? resolveMediaUrl(tagImageUrl) : null);
      } else {
          // Reset to default values when creating new tag
          setTagId(0);
          setTagName(tRef.current('editTags.modal.sampleName'));
          setBackgroundColor('#ffffff');
          setTextColor('#000000');
          setIsPublic(true);
          setTagImage(null);
          setTagImageUrl(null);
        }
      setError(null);
      setRemoveIconOnSave(false);
    }
  }, [tag, isOpen]);

  // The picker's accept list and a dropped file go through the same check.
  const chooseIcon = (file) => {
    if (!file) return;
    if (!isAcceptedIcon(file)) {
      setError(t('editTags.modal.errors.iconType'));
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }
    if (file.size > MAX_ICON_BYTES) {
      setError(t('editTags.modal.errors.iconTooLarge'));
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }
    setError(null);
    setRemoveIconOnSave(false);
    setTagImage(file);
    setTagImageUrl(URL.createObjectURL(file));
  };

  const handleImageUpload = (e) => {
    chooseIcon(e.target.files[0]);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    chooseIcon(e.dataTransfer.files[0]);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    if (loading) return;
    setLoading(true);
    setError(null);

    const formData = {
      id: tagId,
      word: tagName,
      background_color: backgroundColor,
      text_color: textColor,
      is_public: isPublic,
    };

    // Each step can fail on its own; the modal stays open with the reason and
    // closes (through onSave) only once every step has succeeded (ENG-509).
    let savedTagId = tagId;
    try {
      if (tagId !== 0) {
        await fetchWrapperRef.current.put(`${config.apiBaseUrl}/bands/${bandid}/tags/${tagId}`, formData);
      } else {
        const response = await fetchWrapperRef.current.post(`${config.apiBaseUrl}/bands/${bandid}/tags`, formData);
        savedTagId = response?.id || response?.ID || response?.tagId || 0;
        if (!savedTagId) {
          // POST answers 201 with no body: find the new tag by its name. The
          // list comes back as { tags: [...] }, which the old `.find` on the
          // response threw on, so a new tag's icon was never uploaded.
          const tagsResponse = await fetchWrapperRef.current.get(`${config.apiBaseUrl}/bands/${bandid}/tags`);
          const created = tagList(tagsResponse).find((existing) => (existing.word ?? existing.Word) === tagName);
          savedTagId = created?.id ?? created?.ID ?? 0;
        }
        // From here on the tag exists; a retry updates it rather than
        // creating a second one.
        if (savedTagId) setTagId(savedTagId);
      }
    } catch (err) {
      console.error('Error saving tag:', err);
      setError(serverMessage(err) || t('editTags.modal.errors.saveFailed'));
      setLoading(false);
      return;
    }

    try {
      if (tagImage instanceof File) {
        if (!savedTagId) throw new Error('no tag id for the icon upload');
        const imageFormData = new FormData();
        imageFormData.append('file', tagImage);
        await fetchWrapperRef.current.post(`${config.apiBaseUrl}/bands/${bandid}/tags/${savedTagId}/icon`, imageFormData);
        setTagImage(null);
      } else if (removeIconOnSave && savedTagId) {
        await fetchWrapperRef.current.delete(`${config.apiBaseUrl}/bands/${bandid}/tags/${savedTagId}/icon`);
        setRemoveIconOnSave(false);
      }
    } catch (err) {
      console.error('Error saving tag icon:', err);
      setError(err?.code === 'tag_icon_too_large'
        ? t('editTags.modal.errors.iconTooLarge')
        : serverMessage(err) || t('editTags.modal.errors.iconFailed'));
      setLoading(false);
      return;
    }

    setLoading(false);
    if (onSave) {
      onSave();
    } else {
      onRequestClose();
    }
  }, [bandid, backgroundColor, fetchWrapperRef, isPublic, loading, onSave, onRequestClose, removeIconOnSave, t, tagImage, tagName, textColor, tagId]);

  const handleRemoveImage = () => {
    // Clear the file input value to allow re-selecting the same file
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    // A saved tag's icon is removed on Save, not now, so Cancel keeps it.
    if (tagId !== 0 && !(tagImage instanceof File)) {
      setRemoveIconOnSave(true);
    }
    setTagImage(null);
    setTagImageUrl(null);
  };

  return (
    <Modal isOpen={isOpen} onRequestClose={onRequestClose} className="modalContent" overlayClassName="modalOverlay">
      <h2>{tag ? t('editTags.modal.editTitle') : t('editTags.modal.createTitle')}</h2>
      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <label>{t('editTags.modal.nameLabel')}</label>
          <input type="text" value={tagName} onChange={(e) => setTagName(e.target.value)} required />
        </div>
        <div className="form-group color-inputs">
          <div className="color-input">
            <label>{t('editTags.modal.backgroundLabel')}</label>
            <input type="color" value={backgroundColor} onChange={(e) => setBackgroundColor(e.target.value)} required />
          </div>
          <div className="color-input">
            <label>{t('editTags.modal.textLabel')}</label>
            <input type="color" value={textColor} onChange={(e) => setTextColor(e.target.value)} required />
          </div>
        </div>
        <div className="form-group">
          <label>{t('editTags.modal.imageLabel')}</label>
          <div className="image-upload" onDrop={handleDrop} onDragOver={handleDragOver}>
            <div className="upload-section">
              <label htmlFor="file-upload" className="custom-file-upload">
                {t('editTags.modal.upload')}
              </label>
              <input id="file-upload" type="file" ref={fileInputRef} onChange={handleImageUpload} accept='.jpg,.jpeg,.png,image/jpeg,image/png' />
              {tagImageUrl && (
                <div className="image-preview-container">
                  <img src={tagImageUrl} alt={t('editTags.modal.iconPreviewAlt')} className="tag-preview-image" />
                </div>
              )}
            </div>
            {tagImageUrl && (
              <button 
                type="button" 
                className="remove-image-btn"
                onClick={handleRemoveImage}
                title={t('editTags.modal.removeImage')}
              >
                <FaTrash />
              </button>
            )}
          </div>
        </div>
        <div className="form-group switch-group">
            <label>{t('editTags.modal.displayPublicly')}</label>
            <Switch isOn={isPublic} handleToggle={() => setIsPublic(!isPublic)} switchId='switchIsPublic' />
        </div>
        {error && (
          <p className="tag-modal-error" role="alert">{error}</p>
        )}
        <div className="button-group">

          {loading ? (
          <LoadingSpinner />) : (
            <button type="submit" className="btn-save">{t('common.actions.save')}</button>
          )}
          <button type="button" onClick={onRequestClose} className="btn-cancel">{t('common.actions.cancel')}</button>
        </div>
      </form>
      <h3 className="tag-preview-label">{t('editTags.modal.previewHeading')}</h3>
      <div className="tag-preview" style={{ backgroundColor, color: textColor }}>
        {tagImageUrl && <img src={tagImageUrl} alt={t('editTags.modal.previewAlt')} className="tag-preview-image" />}
        <span>{tagName}</span>
      </div>
    </Modal>
  );
};

export default CreateTagModal;